// Create or edit a habit: name, emoji or sticker, color and how often. Existing habits can
// also be paused or deleted here.
import { CircleAlert, LoaderCircle, Minus, Pause, Play, Plus, Trash2 } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent } from 'react';
import {
  HABIT_COLORS,
  NAME_MAX,
  isEmoji,
  normalizeName,
  type Habit,
  type HabitColor,
  type HabitInput,
  type Schedule,
  type Weekday,
} from '../../shared/api';
import { WEEKDAY_SHORT, scheduleLabel } from '../../shared/schedule';
import { errorMessage } from '../lib/api';
import { goBack, parseRoute } from '../lib/router';
import { STICKERS, stickerUrl, type StickerSet } from '../lib/stickers';
import { createHabit, deleteHabit, updateHabit } from '../lib/store';
import { ConfirmDialog } from './ConfirmDialog';
import { Dialog } from './Dialog';
import { EmojiTile } from './EmojiTile';
import { useToast } from './Toasts';

const EMOJIS = [
  '♟️',
  '💧', '🏃', '📚', '🧘', '💤', '🥗', '🍎', '💊', '🦷', '🏋️', '🚴', '🏊', '🚶', '🧠', '✍️', '📖',
  '🎸', '🎨', '🎧', '💻', '🧹', '🛏️', '🌱', '☀️', '🌙', '🙏', '❤️', '😊', '💰', '📵', '🚭', '🍷',
  '☕', '🫖', '🧴', '🐶', '📞', '🧺', '🗣️', '🎯',
];

const STICKER_SETS: { set: StickerSet; label: string }[] = [
  { set: 'classic', label: 'Klassisch' },
  { set: 'everyday', label: 'Alltag' },
  { set: 'chair', label: 'Stuhlgymnastik' },
  { set: 'world', label: 'Weltreise' },
];

const COLOR_NAMES: Record<HabitColor, string> = {
  blue: 'Blau',
  sky: 'Himmelblau',
  teal: 'Türkis',
  green: 'Grün',
  lime: 'Limette',
  amber: 'Bernstein',
  orange: 'Orange',
  rose: 'Rosé',
  pink: 'Pink',
  violet: 'Violett',
};

export const TEMPLATES: HabitInput[] = [
  { name: 'Wasser trinken', emoji: '💧', color: 'sky', schedule: { kind: 'daily' } },
  { name: 'Lesen', emoji: '📚', color: 'violet', schedule: { kind: 'daily' } },
  { name: 'Meditieren', emoji: '🧘', color: 'teal', schedule: { kind: 'daily' } },
  { name: 'Sport', emoji: '🏃', color: 'orange', schedule: { kind: 'weekly', times: 3 } },
  { name: 'Früh schlafen', emoji: '💤', color: 'blue', schedule: { kind: 'weekdays', days: [0, 1, 2, 3, 6] } },
  { name: 'Gesund essen', emoji: '🥗', color: 'green', schedule: { kind: 'daily' } },
  { name: 'Tagebuch', emoji: '✍️', color: 'amber', schedule: { kind: 'daily' } },
  { name: 'Handyfreie Stunde', emoji: '📵', color: 'rose', schedule: { kind: 'daily' } },
];

type Kind = Schedule['kind'];
const KINDS: { kind: Kind; label: string; hint: string }[] = [
  { kind: 'daily', label: 'Täglich', hint: 'Jeden Tag.' },
  { kind: 'weekdays', label: 'Wochentage', hint: 'Nur an den gewählten Tagen.' },
  { kind: 'weekly', label: 'Pro Woche', hint: 'Flexibel: an beliebigen Tagen, Hauptsache das Wochenziel stimmt.' },
];

export interface HabitEditorProps {
  open: boolean;
  /** null: a new habit. */
  habit: Habit | null;
  /** Prefill for a new habit. */
  template?: HabitInput | null;
  onClose: () => void;
}

export function HabitEditor({ open, habit, template, onClose }: HabitEditorProps) {
  const toast = useToast();
  const formId = useId();
  const nameId = useId();
  const errorId = useId();
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('🎯');
  const [customEmoji, setCustomEmoji] = useState('');
  const [sticker, setSticker] = useState<string | null>(null);
  const [iconTab, setIconTab] = useState<'emoji' | 'sticker'>('emoji');
  const [color, setColor] = useState<HabitColor>('blue');
  const [kind, setKind] = useState<Kind>('daily');
  const [weekdays, setWeekdays] = useState<Weekday[]>([0, 1, 2, 3, 4]);
  const [times, setTimes] = useState(3);
  const [dailyTimes, setDailyTimes] = useState(2);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    const source = habit ?? template ?? null;
    setName(source?.name ?? '');
    setEmoji(source?.emoji ?? '🎯');
    setCustomEmoji('');
    setSticker(source?.sticker ?? null);
    setIconTab(source?.sticker ? 'sticker' : 'emoji');
    setColor(source?.color ?? 'blue');
    const schedule = source?.schedule ?? { kind: 'daily' };
    setKind(schedule.kind);
    setWeekdays(schedule.kind === 'weekdays' ? schedule.days : [0, 1, 2, 3, 4]);
    setTimes(schedule.kind === 'weekly' ? schedule.times : 3);
    setDailyTimes(schedule.kind === 'daily' ? schedule.times ?? 1 : 1);
    setError(null);
    setBusy(false);
    setConfirmDelete(false);
  }, [open, habit, template]);

  const applyTemplate = (t: HabitInput) => {
    setName(t.name);
    setEmoji(t.emoji);
    setSticker(t.sticker ?? null);
    setColor(t.color);
    setKind(t.schedule.kind);
    if (t.schedule.kind === 'weekdays') setWeekdays(t.schedule.days);
    if (t.schedule.kind === 'weekly') setTimes(t.schedule.times);
    if (t.schedule.kind === 'daily') setDailyTimes(t.schedule.times ?? 1);
    setError(null);
  };

  const schedule: Schedule =
    kind === 'daily' ? { kind, ...(dailyTimes > 1 ? { times: dailyTimes } : {}) } : kind === 'weekdays' ? { kind, days: [...weekdays].sort((a, b) => a - b) } : { kind, times };

  const toggleWeekday = (day: Weekday) =>
    setWeekdays((list) => (list.includes(day) ? list.filter((d) => d !== day) : [...list, day]));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const cleanName = normalizeName(name);
    if (!cleanName) {
      setError('Gib dem Habit einen Namen.');
      return;
    }
    if (kind === 'weekdays' && weekdays.length === 0) {
      setError('Wähle mindestens einen Wochentag.');
      return;
    }
    setBusy(true);
    setError(null);
    const input: HabitInput = {
      name: cleanName,
      emoji,
      sticker,
      color,
      schedule: kind === 'weekdays' && weekdays.length === 7 ? { kind: 'daily' } : schedule,
    };
    try {
      if (habit) {
        await updateHabit(habit.id, input);
        toast({ text: 'Gespeichert.', kind: 'success' });
      } else {
        await createHabit(input);
        toast({ text: `„${cleanName}“ ist angelegt.`, kind: 'success' });
      }
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const togglePause = async () => {
    if (!habit) return;
    setBusy(true);
    try {
      const archived = !habit.archived;
      await updateHabit(habit.id, { archived });
      onClose();
      toast({
        text: archived ? `„${habit.name}“ pausiert.` : `„${habit.name}“ läuft wieder.`,
        kind: 'success',
        action: { label: 'Rückgängig', run: () => void updateHabit(habit.id, { archived: !archived }).catch(() => {}) },
      });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!habit) return;
    setBusy(true);
    try {
      await deleteHabit(habit.id);
      setConfirmDelete(false);
      onClose();
      const route = parseRoute(window.location.pathname);
      if (route.name === 'habit' && route.id === habit.id) goBack('/habits');
      toast({ text: `„${habit.name}“ wurde gelöscht.`, kind: 'success' });
    } catch (err) {
      setConfirmDelete(false);
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const previewName = normalizeName(name) || 'Neues Habit';
  const showTemplates = !habit && !normalizeName(name);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title={habit ? 'Habit bearbeiten' : 'Neues Habit'}
        footer={
          <>
            {habit && (
              <div className="sheet__foot-extra">
                <button type="button" className="btn btn--danger-ghost btn--sm" onClick={() => setConfirmDelete(true)} disabled={busy}>
                  <Trash2 size={17} /> Löschen
                </button>
                <button type="button" className="btn btn--ghost btn--sm" onClick={togglePause} disabled={busy}>
                  {habit.archived ? <Play size={17} /> : <Pause size={17} />}
                  {habit.archived ? 'Fortsetzen' : 'Pausieren'}
                </button>
              </div>
            )}
            <button type="submit" form={formId} className="btn btn--primary sheet__primary" disabled={busy}>
              {busy && <LoaderCircle size={18} className="spin" aria-hidden="true" />}
              {habit ? 'Speichern' : 'Habit anlegen'}
            </button>
          </>
        }
      >
        <form id={formId} className="editor" onSubmit={submit} noValidate>
          <div className={`editor__preview hc-${color}`} aria-hidden="true">
            <EmojiTile emoji={emoji} sticker={sticker} color={color} size="lg" />
            <div className="editor__preview-text">
              <span className="editor__preview-name">{previewName}</span>
              <span className="editor__preview-meta">
                {kind === 'weekdays' && weekdays.length === 0 ? 'Keine Tage gewählt' : scheduleLabel(schedule)}
              </span>
            </div>
          </div>

          {showTemplates && (
            <div className="editor__section">
              <span className="field__label">Vorschläge</span>
              <div className="chips" role="list">
                {TEMPLATES.map((t) => (
                  <button key={t.name} type="button" role="listitem" className={`chip hc-${t.color}`} onClick={() => applyTemplate(t)}>
                    <span aria-hidden="true">{t.emoji}</span> {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="field">
            <label htmlFor={nameId} className="field__label">
              Name
            </label>
            <input
              id={nameId}
              className="field__input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="z. B. 10 Minuten lesen"
              maxLength={NAME_MAX + 10}
              autoComplete="off"
              enterKeyHint="done"
              aria-invalid={error === 'Gib dem Habit einen Namen.' ? true : undefined}
              aria-describedby={error ? errorId : undefined}
              autoFocus={!!habit || window.matchMedia('(pointer: fine)').matches}
            />
          </div>

          <div className="editor__section">
            <span className="field__label" id={`${formId}-emoji`}>
              Symbol
            </span>
            <div className="segmented" role="radiogroup" aria-label="Art des Symbols">
              <button type="button" role="radio" aria-checked={iconTab === 'emoji'} onClick={() => setIconTab('emoji')}>
                Emoji
              </button>
              <button type="button" role="radio" aria-checked={iconTab === 'sticker'} onClick={() => setIconTab('sticker')}>
                Sticker
              </button>
            </div>
            {iconTab === 'emoji' ? (
              <>
                <div className={`emoji-grid hc-${color}`} role="radiogroup" aria-labelledby={`${formId}-emoji`}>
                  {[...(EMOJIS.includes(emoji) ? [] : [emoji]), ...EMOJIS].map((e) => (
                    <button
                      key={e}
                      type="button"
                      role="radio"
                      aria-checked={!sticker && emoji === e}
                      className="emoji-grid__item"
                      onClick={() => {
                        setEmoji(e);
                        setSticker(null);
                      }}
                    >
                      {e}
                    </button>
                  ))}
                </div>
                <input
                  className="field__input field__input--compact"
                  value={customEmoji}
                  placeholder="Anderes Emoji eingeben …"
                  aria-label="Anderes Emoji"
                  onChange={(event) => {
                    const value = event.target.value;
                    const first = [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(value)].at(-1)?.segment ?? '';
                    setCustomEmoji(first);
                    if (isEmoji(first)) {
                      setEmoji(first);
                      setSticker(null);
                    }
                  }}
                />
              </>
            ) : (
              <div className={`sticker-grid hc-${color}`} role="radiogroup" aria-labelledby={`${formId}-emoji`}>
                {STICKER_SETS.map(({ set, label }) => (
                  <div key={set} className="sticker-grid__group" role="presentation">
                    <span className="sticker-grid__label">{label}</span>
                    <div className="sticker-grid__items" role="presentation">
                      {STICKERS.filter((s) => s.set === set).map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          role="radio"
                          aria-checked={sticker === s.id}
                          aria-label={s.label}
                          title={s.label}
                          className="sticker-grid__item"
                          onClick={() => setSticker(s.id)}
                        >
                          <img src={stickerUrl(s.id)} alt="" loading="lazy" decoding="async" draggable={false} />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {sticker && iconTab === 'sticker' && (
              <button type="button" className="btn btn--ghost btn--sm editor__clear-sticker" onClick={() => setSticker(null)}>
                Sticker entfernen, wieder {emoji} zeigen
              </button>
            )}
          </div>

          <div className="editor__section">
            <span className="field__label" id={`${formId}-color`}>
              Farbe
            </span>
            <div className="swatches" role="radiogroup" aria-labelledby={`${formId}-color`}>
              {HABIT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={color === c}
                  aria-label={COLOR_NAMES[c]}
                  title={COLOR_NAMES[c]}
                  className={`swatch hc-${c}`}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>

          <div className="editor__section">
            <span className="field__label" id={`${formId}-freq`}>
              Häufigkeit
            </span>
            <div className="segmented" role="radiogroup" aria-labelledby={`${formId}-freq`}>
              {KINDS.map((k) => (
                <button key={k.kind} type="button" role="radio" aria-checked={kind === k.kind} onClick={() => setKind(k.kind)}>
                  {k.label}
                </button>
              ))}
            </div>
            <p className="field__hint">{KINDS.find((k) => k.kind === kind)?.hint}</p>

            {kind === 'weekdays' && (
              <div className={`weekday-picker hc-${color}`} role="group" aria-label="Wochentage">
                {WEEKDAY_SHORT.map((label, i) => (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={weekdays.includes(i as Weekday)}
                    className="weekday-picker__day"
                    onClick={() => toggleWeekday(i as Weekday)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            {kind === 'daily' && (
              <div className="stepper">
                <button type="button" className="icon-btn icon-btn--glass" onClick={() => setDailyTimes((t) => Math.max(1, t - 1))} disabled={dailyTimes <= 1} aria-label="Weniger">
                  <Minus size={18} />
                </button>
                <span className="stepper__value" aria-live="polite">
                  <strong>{dailyTimes}×</strong> pro Tag
                </span>
                <button type="button" className="icon-btn icon-btn--glass" onClick={() => setDailyTimes((t) => Math.min(20, t + 1))} disabled={dailyTimes >= 20} aria-label="Mehr">
                  <Plus size={18} />
                </button>
              </div>
            )}

            {kind === 'weekly' && (
              <div className="stepper">
                <button type="button" className="icon-btn icon-btn--glass" onClick={() => setTimes((t) => Math.max(1, t - 1))} disabled={times <= 1} aria-label="Weniger">
                  <Minus size={18} />
                </button>
                <span className="stepper__value" aria-live="polite">
                  <strong>{times}×</strong> pro Woche
                </span>
                <button type="button" className="icon-btn icon-btn--glass" onClick={() => setTimes((t) => Math.min(6, t + 1))} disabled={times >= 6} aria-label="Mehr">
                  <Plus size={18} />
                </button>
              </div>
            )}
          </div>

          {error && (
            <p id={errorId} className="inline-error" role="alert">
              <CircleAlert size={18} aria-hidden="true" />
              {error}
            </p>
          )}
        </form>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete}
        title="Habit löschen?"
        confirmLabel="Endgültig löschen"
        danger
        busy={busy}
        onConfirm={remove}
        onClose={() => setConfirmDelete(false)}
      >
        <p>
          „{habit?.name}“ und sein gesamter Verlauf werden gelöscht. Das lässt sich nicht rückgängig machen.
        </p>
        {!habit?.archived && <p className="confirm__hint">Tipp: Mit „Pausieren“ verschwindet das Habit aus „Heute“, der Verlauf bleibt erhalten.</p>}
      </ConfirmDialog>
    </>
  );
}
